/**
 * ¿Se puede leer texto blanco encima de este color?
 *
 * ── POR QUÉ ESTO VIVE EN EL CORE Y NO EN LA PANTALLA ────────────────────────────
 *
 * Lo usan dos sitios y **tienen que coincidir**:
 *
 *   · La API, al guardar la marca de un cliente: para AVISARLE de que su color no se va a
 *     poder usar.
 *   · La pantalla, al pintarla: para APLICAR el defecto en vez del color ilegible.
 *
 * Si el aviso usara un criterio y la pantalla otro, el cliente vería una advertencia que no
 * corresponde con lo que pasa — o, peor, ninguna advertencia y el botón invisible. Una regla,
 * un sitio.
 *
 * (Estaba en `apps/verify/src/marca.ts` y la API intentó importarla de ahí. Un backend no
 * puede importar del front, y el intento señaló el problema real: no era una función de
 * pantalla, era una regla del producto.)
 *
 * ── LA REGLA, Y POR QUÉ ES ESE NÚMERO ──────────────────────────────────────────
 *
 * 4.5:1 es el mínimo de WCAG AA para texto normal. No es una preferencia estética: es el
 * umbral por debajo del cual una parte real de la gente, con una pantalla real y a la luz del
 * sol, no lee el botón.
 *
 * Y a plena luz del sol es exactamente donde la gente escanea su DNI.
 *
 * ── QUÉ PASA CON EL COLOR QUE NO PASA ──────────────────────────────────────────
 *
 * Se guarda igual —es su marca, y no somos nadie para rechazársela— pero la pantalla usa el
 * defecto y al cliente se le avisa. Guardar y aplicar son cosas distintas; confundirlas sería
 * o mentirle (guardar y callar) o quitarle su marca sin explicación.
 */

/** Luminancia relativa (WCAG 2.1). `null` si el color no se entiende. */
function luminancia(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;

  const n = parseInt(m[1]!, 16);
  const canal = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * canal((n >> 16) & 255) + 0.7152 * canal((n >> 8) & 255) + 0.0722 * canal(n & 255);
}

/**
 * El mínimo de WCAG AA para texto normal. Fijado por un test a propósito: bajarlo es una
 * decisión que alguien tiene que tomar mirándola, no un número que se ajusta hasta que el
 * color del cliente pase.
 */
export const CONTRASTE_MINIMO = 4.5;

export function contrastaConBlanco(hex: string): boolean {
  const l = luminancia(hex);
  // Lo que no se entiende no se usa. Meter basura en `--acento` dejaría el CSS con un valor
  // inválido y el botón sin color: transparente sobre blanco, invisible. Es la misma regla
  // que las fechas de OFAC — lo que no se entiende no se convierte en un dato, se declara
  // ignorado. Y aquí ignorar significa quedarse con el morado del Figma, que funciona.
  if (l === null) return false;
  return 1.05 / (l + 0.05) >= CONTRASTE_MINIMO;
}
